import React, { useEffect, useState, useCallback } from 'react';
import { timeAgo, fmtDate, fmtDateTime, parseUTC } from '../utils/dateUtils';
import { useParams, useNavigate } from 'react-router-dom';
import { employeeManagementApi, taskApi, userApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { Btn, Alert, Spinner, RoleBadge, StatusBadge, PriorityBadge } from '../components/ui';

// ── Reassign tasks modal ───────────────────────────────────────────────────────
function ReassignTasksModal({ tasks, employees, onClose, onDone }) {
  const [assignments, setAssignments] = useState({});
  const [saving, setSaving]           = useState(false);
  const [error,  setError]            = useState('');

  const set = (taskId, empId) => setAssignments(a => ({ ...a, [taskId]: empId }));
  const allAssigned = tasks.every(t => assignments[t.id]);

  const submit = async () => {
    if (!allAssigned) { setError('Assign every task before proceeding'); return; }
    setSaving(true); setError('');
    try {
      await Promise.all(tasks.map(t => taskApi.update(t.id, { assignedToId: assignments[t.id] })));
      onDone();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to reassign tasks');
    } finally { setSaving(false); }
  };

  return (
    <div style={O.overlay}>
      <div style={{ ...O.modal, maxWidth: '560px' }}>
        <div style={O.header}>
          <h3 style={O.title}>Reassign Open Tasks</h3>
          <button onClick={onClose} style={O.close}>×</button>
        </div>
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '16px' }}>
          All {tasks.length} active task{tasks.length !== 1 ? 's' : ''} must be reassigned before this employee can be deactivated or deleted.
        </p>
        {error && <Alert type="error" message={error} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px', maxHeight: '340px', overflowY: 'auto' }}>
          {tasks.map(task => (
            <div key={task.id} style={{ padding: '12px 14px', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <PriorityBadge priority={task.priority} />
                <StatusBadge   status={task.status}     />
              </div>
              <p style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>{task.title}</p>
              <select
                value={assignments[task.id] || ''}
                onChange={e => set(task.id, e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-border)', fontSize: '13px' }}
              >
                <option value="">— Assign to —</option>
                {employees.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.name} ({e.employeeId}){e.away ? ' — Away' : ''}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} loading={saving} disabled={!allAssigned}>
            Reassign All & Continue
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── Away modal ────────────────────────────────────────────────────────────────
function AwayModal({ employee, onClose, onDone }) {
  const [returnDate, setReturnDate] = useState('');
  const [saving, setSaving]         = useState(false);
  const [error,  setError]          = useState('');
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await employeeManagementApi.markAway(employee.id, returnDate || null);
      onDone();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to mark Away');
    } finally { setSaving(false); }
  };

  return (
    <div style={O.overlay}>
      <div style={{ ...O.modal, maxWidth: '380px' }}>
        <div style={O.header}>
          <h3 style={O.title}>Mark as Away</h3>
          <button onClick={onClose} style={O.close}>×</button>
        </div>
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '16px' }}>
          Marking <strong>{employee.name}</strong> as Away. Tasks assigned to them will show a warning to anyone assigning new work.
        </p>
        {error && <Alert type="error" message={error} />}
        <div style={{ marginBottom: '16px' }}>
          <label style={O.label}>Expected return date (optional)</label>
          <input type="date" min={tomorrow} value={returnDate}
            onChange={e => setReturnDate(e.target.value)}
            style={{ ...O.input, marginTop: '6px' }} />
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} loading={saving}>Confirm Away</Btn>
        </div>
      </div>
    </div>
  );
}

// ── Delete request modal ──────────────────────────────────────────────────────
function DeleteRequestModal({ employee, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await employeeManagementApi.requestDeletion(employee.id, reason);
      onDone();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to submit request');
    } finally { setSaving(false); }
  };

  return (
    <div style={O.overlay}>
      <div style={{ ...O.modal, maxWidth: '420px' }}>
        <div style={O.header}>
          <h3 style={{ ...O.title, color: 'var(--color-danger)' }}>Request Employee Deletion</h3>
          <button onClick={onClose} style={O.close}>×</button>
        </div>
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '8px' }}>
          You are requesting deletion of <strong>{employee.name}</strong> ({employee.employeeId}).
          This will be sent to Super Admin for approval.
        </p>
        <div style={{ padding: '10px 14px', background: '#FFF5F5', border: '1px solid #FEB2B2', borderRadius: 'var(--radius-sm)', marginBottom: '14px', fontSize: '12px', color: 'var(--color-danger)' }}>
          The employee's task history will be permanently preserved. Their account will be archived, not erased.
        </div>
        {error && <Alert type="error" message={error} />}
        <div style={{ marginBottom: '16px' }}>
          <label style={O.label}>Reason for deletion request</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)}
            placeholder="e.g. Employee has left the company"
            rows={3} style={{ ...O.input, resize: 'vertical', marginTop: '6px' }} />
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="danger" onClick={submit} loading={saving}>Submit Deletion Request</Btn>
        </div>
      </div>
    </div>
  );
}

// ── Main profile page ─────────────────────────────────────────────────────────
export default function EmployeeProfile() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { user: currentUser } = useAuth();

  const [profile,        setProfile]       = useState(null);
  const [tasks,          setTasks]         = useState([]);
  const [allEmployees,   setAllEmployees]  = useState([]);
  const [loading,        setLoading]       = useState(true);
  const [error,          setError]         = useState('');
  const [success,        setSuccess]       = useState('');
  const [activeTab,      setActiveTab]     = useState('tasks');

  const [showAway,       setShowAway]      = useState(false);
  const [showDelete,     setShowDelete]    = useState(false);
  const [showReassign,   setShowReassign]  = useState(false);
  const [pendingAction,  setPendingAction] = useState(null); // 'deactivate' | 'delete-request'

  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';

  const load = useCallback(() => {
    Promise.all([
      employeeManagementApi.getProfile(id),
      userApi.getAll(),
    ])
      .then(([p, u]) => {
        setProfile(p.data.data.employee);
        setTasks(p.data.data.tasks || []);
        setAllEmployees((u.data.data || []).filter(e => e.id !== id && e.active));
      })
      .catch(() => setError('Failed to load profile'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const activeTasks = tasks.filter(t => !['DONE'].includes(t.status));

  // Deactivate — check open tasks first
  const handleMfaReset = async () => {
    if (!window.confirm('Reset MFA for this employee? They will need to set up MFA again on next login.')) return;
    try {
      await mfaApi.adminReset(id);
      setSuccess('MFA reset. Employee will need to set up MFA again.');
    } catch (e) { setError(e.response?.data?.message || 'Failed to reset MFA'); }
  };

  const handleDeactivate = () => {
    if (activeTasks.length > 0) {
      setPendingAction('deactivate');
      setShowReassign(true);
    } else {
      doDeactivate();
    }
  };

  const doDeactivate = async () => {
    setError(''); setSuccess('');
    try {
      await userApi.deactivate(id);
      setSuccess('Employee deactivated successfully');
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to deactivate');
    }
  };

  const handleActivate = async () => {
    setError(''); setSuccess('');
    try {
      await userApi.activate(id);
      setSuccess('Employee reactivated successfully');
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to activate');
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm(`Reset password for ${profile?.name} to the company default? They will be forced to change it on next login.`)) return;
    setError(''); setSuccess('');
    try {
      await userApi.resetPassword(id);
      setSuccess('Password reset to default. Employee must reset on next login.');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleMarkBack = async () => {
    setError(''); setSuccess('');
    try {
      await employeeManagementApi.markBack(id);
      setSuccess('Employee marked as back');
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed');
    }
  };

  const handleDeleteRequest = () => {
    if (activeTasks.length > 0) {
      setPendingAction('delete-request');
      setShowReassign(true);
    } else {
      setShowDelete(true);
    }
  };

  const afterReassign = () => {
    setShowReassign(false);
    load();
    if (pendingAction === 'deactivate')    doDeactivate();
    if (pendingAction === 'delete-request') setShowDelete(true);
    setPendingAction(null);
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner size={28} /></div>
  );
  if (!profile) return (
    <div style={{ padding: '24px' }}><Alert type="error" message="Employee not found" /></div>
  );

  const TABS = [
    { key: 'tasks',   label: `Tasks (${tasks.length})` },
    { key: 'active',  label: `Active (${activeTasks.length})` },
  ];

  const shownTasks = activeTab === 'tasks' ? tasks : activeTasks;

  return (
    <div style={{ padding: '24px', maxWidth: '720px' }}>
      {/* Back */}
      <button onClick={() => navigate(-1)} style={S.backBtn}>← Back</button>

      {/* Alerts */}
      {error   && <Alert type="error"   message={error}   />}
      {success && <Alert type="success" message={success} />}

      {/* Profile card */}
      <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', padding: '24px', marginTop: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'var(--color-primary-lighter)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: 700, flexShrink: 0 }}>
              {profile.name?.[0] || '?'}
            </div>
            <div>
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--color-text)', marginBottom: '4px' }}>{profile.name}</h2>
              <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '8px' }}>{profile.employeeId}</p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <RoleBadge role={profile.role} />
                {!profile.active && <span style={{ fontSize: '11px', background: '#EDF2F7', color: '#718096', padding: '2px 8px', borderRadius: '999px', fontWeight: 600 }}>INACTIVE</span>}
                {profile.away && (
                  <span style={{ fontSize: '11px', background: '#FFFBEB', color: '#D69E2E', padding: '2px 8px', borderRadius: '999px', fontWeight: 600 }}>
                    AWAY{profile.awayReturnDate ? ` · returns ${profile.awayReturnDate}` : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Meta */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginTop: '20px', padding: '16px', background: 'var(--color-bg)', borderRadius: 'var(--radius-md)' }}>
          <MetaItem label="Departments" value={profile.departments?.map(d => d.name).join(', ') || '—'} />
          <MetaItem label="Member since" value={profile.createdAt ? fmtDate(profile.createdAt) : '—'} />
          <MetaItem label="Last login"   value={timeAgo(profile.lastLoginAt)} />
          <MetaItem label="Total tasks"  value={tasks.length} />
          <MetaItem label="Active tasks" value={activeTasks.length} />
          <MetaItem label="Done tasks"   value={tasks.filter(t => t.status === 'DONE').length} />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px' }}>
          <Btn size="sm" variant="secondary" onClick={handleResetPassword}>Reset Password</Btn>

          {profile.away
            ? <Btn size="sm" variant="secondary" onClick={handleMarkBack}>Mark Back</Btn>
            : <Btn size="sm" variant="secondary" onClick={() => setShowAway(true)}>Mark Away</Btn>
          }

          {profile.active
            ? <Btn size="sm" variant="secondary" onClick={handleDeactivate}>Deactivate</Btn>
            : <Btn size="sm" variant="secondary" onClick={handleActivate}>Reactivate</Btn>
          }

          <Btn size="sm" variant="danger" onClick={handleDeleteRequest}>Request Deletion</Btn>
        </div>
      </div>

      {/* Tasks */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: '16px' }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '14px', fontWeight: activeTab === tab.key ? 600 : 400,
            color: activeTab === tab.key ? 'var(--color-primary)' : 'var(--color-text-muted)',
            borderBottom: `2px solid ${activeTab === tab.key ? 'var(--color-primary)' : 'transparent'}`,
          }}>{tab.label}</button>
        ))}
      </div>

      {shownTasks.length === 0 ? (
        <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', padding: '16px 0' }}>No tasks.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {shownTasks.map(task => (
            <div key={task.id} onClick={() => navigate(`/tasks/${task.id}`)} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
              padding: '10px 14px', background: 'var(--color-surface)',
              borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)',
              cursor: 'pointer', transition: 'box-shadow 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-sm)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '14px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.title}</p>
                <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                  Due {task.deadline}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                <PriorityBadge priority={task.priority} />
                <StatusBadge   status={task.status}     />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showReassign && (
        <ReassignTasksModal
          tasks={activeTasks}
          employees={allEmployees}
          onClose={() => { setShowReassign(false); setPendingAction(null); }}
          onDone={afterReassign}
        />
      )}
      {showAway && (
        <AwayModal
          employee={profile}
          onClose={() => setShowAway(false)}
          onDone={() => { setShowAway(false); load(); setSuccess('Employee marked as Away'); }}
        />
      )}
      {showDelete && (
        <DeleteRequestModal
          employee={profile}
          onClose={() => setShowDelete(false)}
          onDone={() => { setShowDelete(false); setSuccess('Deletion request submitted to Super Admin'); load(); }}
        />
      )}
    </div>
  );
}

function MetaItem({ label, value }) {
  return (
    <div>
      <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>{label}</p>
      <p style={{ fontSize: '14px', color: 'var(--color-text)', fontWeight: 500 }}>{String(value)}</p>
    </div>
  );
}

const S = {
  backBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'var(--color-text-muted)', padding: 0, marginBottom: '4px' },
};
const O = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '16px' },
  modal:   { background: '#fff', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' },
  title:   { fontSize: '17px', fontWeight: 600, color: 'var(--color-primary)' },
  close:   { background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--color-text-muted)' },
  input:   { width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px', color: 'var(--color-text)', background: 'var(--color-surface)' },
  label:   { fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)' },
};
